/**
 * `WorkflowAuthoredView.ts` — the MD-like authoring projection of a workflow
 * document.
 *
 * The stored document is pure YAML (`WorkflowYaml.ts`); what the WYSIWYG
 * editor shows is a human-readable TEXT VIEW rendered from the model:
 *
 * ```
 * # Workflow: Robodog Familiarization
 *
 * Name: Robodog Familiarization
 * Description: General familiarity with the Robodog
 * Takes about: 5 min
 *
 * ## Group: Getting started
 *
 * Name: Getting started
 *
 * ## Step: Overview
 *
 * Name: Overview
 * Scene: Scene 1
 * Description: Covers the fan housing and its two clips.
 *
 * Instructions:
 * Undo the two clips, then lift the housing straight off.
 *
 * - Loosen the left clip
 * - Loosen the right clip
 *
 * Back button: Back
 * Next button: Next
 *
 * Triggers:
 * Button "Explore the fan" -> Fan Assembly
 * ```
 *
 * The view is a PROJECTION, not the document: `renderAuthoredView` derives it,
 * the editor edits it, and `parseAuthoredView` + `applyAuthoredView` translate
 * the edited text back into the YAML model. Machine-owned content (step ids,
 * prepared `scripts`, identity, `workflow_state`) never renders here and is
 * carried across an edit by matching steps — by name first, then positionally
 * among the unmatched, so a step renamed in the editor KEEPS its prepared
 * scripts instead of orphaning them.
 *
 * Unmodelled lines are dropped on parse-back. Structural labels are locked in
 * the editor so that is rare; retired keys still terminate a wrapped field
 * instead of folding into the value above them.
 *
 * Grammar invariants shared with the editor lexers (`workflowEditorExtensions`
 * + `WorkflowView.vue`) and the Tiptap mapping (`workflowDocMapping`):
 * headings are `#`/`##` lines, fields are `Key: value` lines, trigger buttons
 * are numbered `N. text` lines under `Triggers:`. Change them together or the
 * views desync.
 *
 * Pure logic — no DOM, no Vue, no IOC.
 */

import {
  findAuthoredScript,
  formatCommandEntry,
  isCommandBlockRenderable,
  parseCommandEntry,
} from "./WorkflowCommandLine";
import {
  WorkflowDocument,
  WorkflowGroup,
  WorkflowScript,
  WorkflowStep,
  WorkflowStepAttachment,
  WorkflowStepMedia,
  WorkflowStepTrigger,
  cloneScript,
  cloneStepView,
  equalsIgnoreCase,
  getStepNextButtonLabel,
  isCompletionStep,
  normalizeMediaFit,
  normalizeMediaType,
  normalizeNextTarget,
  trimChars,
} from "./WorkflowDocument";
import {
  WorkflowPanelDock,
  WorkflowPanelPlacement,
  WorkflowPanelScreenDock,
  WorkflowUiState,
} from "./WorkflowDocument";

// ---------------------------------------------------------------------------
// Grammar constants
// ---------------------------------------------------------------------------

export const TITLE_PREFIX = "# Workflow:";
export const STEP_HEADING_PREFIX = "Step:";
export const GROUP_HEADING_PREFIX = "Group:";
/** Label opening a step's trigger-button block. */
export const TRIGGERS_LABEL = "Triggers";
/**
 * Label opening a step's prepared-command block — the on-start script's steps,
 * rendered as numbered lines exactly like the trigger buttons below them.
 *
 * A step's OTHER scripts (a `manual` one the agent plays by name) are machine
 * data and are carried across an edit, like every other unrendered field. One
 * block per step keeps this singular the way `Triggers:` is; a second labelled
 * block per script would be a grammar nobody asked for.
 */
export const COMMANDS_LABEL = "Commands";

/**
 * Label opening a step's learner-facing prose — `step_instructions` in the
 * file, rendered as a BLOCK rather than as a `Key: value` line.
 *
 * It is a block because the value is MARKUP (2026-08-27): paragraphs separated
 * by blank lines, `- ` bullets, `1. ` numbered items, `**bold**` and `` `code` ``,
 * which the play panel renders through `MarkdownView`. None of that survives an
 * inline field — {@link appendFieldContinuations} joins wrapped lines with a
 * SPACE and stops dead at a blank line or a `-`, so a bulleted instruction
 * collapsed to one run-on line and everything after the first blank was
 * DROPPED. So the value needs a block, and once it has one it may as well share
 * the shape `Commands:` and `Triggers:` already have: a label, its content
 * below it, and the block ending where the next structure begins.
 *
 * It renders in `Description:`'s own slot rather than at the bottom of the
 * field run, set off by blank lines. The two are the step's PAIR of prose
 * fields — one written for the guide, one for the learner, and the standing
 * instruction to authors and to the agent is not to write the same words into
 * both — so they are worth keeping adjacent even though the block interrupts
 * the compact field table.
 *
 * What the block deliberately cannot carry is a `#` HEADING, because
 * `parseAuthoredView` splits sections on `##` and one typed here would saw the
 * step in half. A stored value containing one is not rendered at all and is
 * carried across the edit instead ({@link isInstructionsBlockRenderable}) —
 * the same "hide the block, keep the data" rule an unreadable command entry
 * already follows. A step's name is already the panel's heading, so instructions
 * have nothing to head.
 */
export const INSTRUCTIONS_LABEL = "Instructions";

/**
 * Label opening a step's video TRANSCRIPT — `media.transcript` in the file,
 * rendered as a BLOCK rather than as a `Key: value` line.
 *
 * A block for the same reason {@link INSTRUCTIONS_LABEL} is one, and it was an
 * inline field until 2026-09-02: a transcript is what a person SAID, so it is
 * paragraphs, and none of that survives an inline field.
 * {@link appendFieldContinuations} joins wrapped lines with a SPACE and stops
 * dead at a blank line, so the damage was measured rather than argued — a
 * three-paragraph transcript rendered, parsed back, and came out as its first
 * sentence, with everything after the first blank line silently DELETED on the
 * next WYSIWYG save. The renderer's own line metadata was short by one entry
 * per embedded newline too, which slid every decoration below the transcript
 * onto the wrong row.
 *
 * Unlike `Instructions:`, which stays mid-run beside the prose field it is
 * paired with, this renders LAST in the media run. `Media start:` / `Media
 * end:` / `Media loop:` / `Media autoplay:` are settings about the same clip and a reader asking
 * "where does it start" should not have to scroll a transcript to find out;
 * putting the long prose at the bottom is also how `Commands:` and `Triggers:`
 * already sit. The FILE's key order is unchanged — parse-back matches on the
 * label, not on position.
 *
 * What the block cannot carry is the same pair `Instructions:` reserves, and
 * for the same reasons: a `#` HEADING line (`parseAuthoredView` splits sections
 * on `##`, so one here saws the step in two) and a line shaped like one of the
 * view's own LABELS (it closes the block). A stored value containing either is
 * not rendered at all and is carried across the edit
 * ({@link isTranscriptBlockRenderable}).
 */
export const MEDIA_TRANSCRIPT_LABEL = "Media transcript";

/**
 * Every name — the workflow's, a group's, a step's — is authored HERE, in the
 * section's own `Name:` field, and never in the `#`/`##` heading above it. The
 * heading is DERIVED from the field and locked in the editor: it anchors the
 * section split and the fold toggle, so letting it be typed into makes the
 * document's structure something the author can break by accident. Field-first
 * also makes a name behave like every other value in its section, which is why
 * groups and the workflow header joined steps in this on 2026-08-10.
 */
const K_NAME_KEY = "Name";
const K_SCENE_KEY = "Scene";
const K_TAKES_ABOUT_KEY = "Takes about";
const K_LANGUAGE_KEY = "Language";
const K_PRODUCT_KEY = "Product";
/**
 * The WORKFLOW HEADER's blurb, `description` in the file. Header-only since
 * 2026-08-27: a step's counterpart is {@link K_STEP_CONTEXT_KEY}. It stays in
 * {@link K_STEP_LABEL_KEYS} even so — see the note there.
 */
const K_DESCRIPTION_KEY = "Description";
/**
 * A step's INTERNAL summary, `step_context` in the file. Labelled `Context:`
 * rather than `Step context:` for the same reason `Instructions:` drops its
 * first word: it renders inside a `## Step:` section, where that word only
 * restates the heading — the label-vs-key licence `Takes about:` / `takes_about`
 * already takes.
 *
 * Naming it apart from the header's `Description:` is the point of the rename,
 * not a side effect: one label served both regions, so the two fields read as
 * one the format happened to allow twice, and the editor could only offer them
 * a single hint that had to be true of both.
 */
const K_STEP_CONTEXT_KEY = "Context";
/**
 * The learner-facing copy, `step_instructions` in the file. Labelled
 * `Instructions:` rather than `Step instructions:` because it renders INSIDE a
 * `## Step:` section, where the first word would only restate the heading —
 * the same label-vs-key licence `Takes about:` / `takes_about` already takes.
 *
 * A BLOCK label since 2026-08-27, not an inline field — see
 * {@link INSTRUCTIONS_LABEL} for why.
 */
const K_INSTRUCTIONS_KEY = INSTRUCTIONS_LABEL;
const K_BACK_BUTTON_KEY = "Back button";
const K_NEXT_BUTTON_KEY = "Next button";
/**
 * `next_target` in the file: where this step's Next goes. Rendered directly
 * under `Next button:` on any step that HAS a Next button and is not an ending
 * (empty, with a ghost
 * hint, when the file says nothing), and on any step that already carries a
 * value — so the "Next target without a Next button" error has a row to point
 * at. Empty parses back as ABSENT ({@link normalizeNextTarget}), which is what
 * keeps the empty line from writing `next_target: ""` into the file.
 */
const K_NEXT_TARGET_KEY = "Next goes to";
const K_ATTACHMENT_KEY = "Attachment point";

/**
 * A step's media renders as NINE sibling `Key: value` lines plus one BLOCK
 * ({@link MEDIA_TRANSCRIPT_LABEL}), rather than one compact line the way
 * `Attachment point:` does. An attachment is a reference plus three numbers and
 * fits on a line the author reads more than edits; media carries free prose,
 * so a compact form could not hold it — and every one of these is a value the
 * author sets by hand. `MEDIA_FIELD_KEYS` is the render order of the inline
 * nine; the transcript block renders after them.
 */
const K_MEDIA_TYPE_KEY = "Media type";
const K_MEDIA_FIT_KEY = "Media fit";
const K_MEDIA_URL_KEY = "Media url";
const K_MEDIA_NAME_KEY = "Media name";
const K_MEDIA_DESCRIPTION_KEY = "Media description";
const K_MEDIA_START_KEY = "Media start";
const K_MEDIA_END_KEY = "Media end";
const K_MEDIA_LOOP_KEY = "Media loop";
const K_MEDIA_AUTOPLAY_KEY = "Media autoplay";

export const MEDIA_FIELD_KEYS: ReadonlyArray<string> = [
  K_MEDIA_TYPE_KEY,
  K_MEDIA_FIT_KEY,
  K_MEDIA_URL_KEY,
  K_MEDIA_NAME_KEY,
  K_MEDIA_DESCRIPTION_KEY,
  K_MEDIA_START_KEY,
  K_MEDIA_END_KEY,
  K_MEDIA_LOOP_KEY,
  K_MEDIA_AUTOPLAY_KEY,
];

const K_HEADING_REGEX = /^(#{1,6})\s+(.*)$/;
const K_STEP_LINE_REGEX = /^\s*(\d+)\.\s+(.*)$/;
const K_TRIGGER_LABEL_REGEX = /^On\s+[^:]+:\s*$/;
/**
 * One authored trigger line: `Button "<label>" -> <step or group>`. The label
 * is REQUIRED to be quoted, which is what keeps the arrow unambiguous. `→` is
 * accepted alongside `->` because the WYSIWYG editor is a contenteditable
 * surface where an OS text substitution can produce the real arrow.
 */
const K_TRIGGER_BUTTON_REGEX = /^Button\s+"([^"]*)"\s*(?:->|→)\s*(.+)$/i;

/**
 * The `Key: value` fields of a step — the labels the inline field reader
 * handles. `Instructions` is deliberately ABSENT: it is a block label now
 * ({@link INSTRUCTIONS_LABEL}) and {@link parseInstructions} owns it, so
 * listing it here would give it a second reader that joins its lines with a
 * space and stops at the first blank.
 */
const K_STEP_FIELD_KEYS: ReadonlyArray<string> = [
  K_NAME_KEY,
  K_SCENE_KEY,
  K_TAKES_ABOUT_KEY,
  K_STEP_CONTEXT_KEY,
  K_BACK_BUTTON_KEY,
  K_NEXT_BUTTON_KEY,
  K_NEXT_TARGET_KEY,
  K_ATTACHMENT_KEY,
  ...MEDIA_FIELD_KEYS,
];

/**
 * Every label a step section can carry — the inline fields PLUS the block
 * labels. This is what "is this line structure?" is asked against
 * ({@link isAnyKnownLabel}), which is a wider question than "does the inline
 * field reader handle it": `Instructions:` must still terminate the field above
 * it and close a prose block, even though nothing in the field loop reads it.
 */
const K_STEP_LABEL_KEYS: ReadonlyArray<string> = [
  ...K_STEP_FIELD_KEYS,
  K_INSTRUCTIONS_KEY,
  // A BLOCK label like `Instructions:`, so it is deliberately NOT in
  // `K_STEP_FIELD_KEYS` — listing it there would give it a second reader that
  // joins its lines with a space and stops at the first blank, which is the
  // exact truncation the block exists to fix. It still has to be a known
  // LABEL: it terminates the field above it and closes a prose block.
  MEDIA_TRANSCRIPT_LABEL,
  // A HEADER field, listed here because this set answers "is this line
  // structure?" rather than "does the step's field reader handle it". A
  // `Description:` line typed into a step section reads as no step field, so it
  // is dropped — but it must still TERMINATE the field above it, or its text
  // folds into that value as a wrapped continuation and silently corrupts it.
  // The same is arguably true of the other header-only labels (`Language:`,
  // `Product:`), which is pre-existing and left alone here.
  K_DESCRIPTION_KEY,
];

/**
 * Field labels the format no longer models but still RECOGNISES as labels.
 * A retired key must stay here or its line folds into the field above it as a
 * wrapped continuation; listed, the line stays inert (dropped, not continued).
 */
const K_RETIRED_FIELD_KEYS: ReadonlyArray<string> = ["Done when", "Guide context", "On Start"];

// ---------------------------------------------------------------------------
// Line metadata (what the editor's decorations anchor on)
// ---------------------------------------------------------------------------

/**
 * Structural address of one rendered/parsed view line. `step` is null for
 * header and group-heading lines; `field` names the authored field or block
 * the line belongs to (`name` for the heading line itself).
 */
export interface WorkflowViewLineMeta {
  readonly step: string | null;
  readonly field: string | null;
  readonly group: string | null;
}

const K_BLANK_META: WorkflowViewLineMeta = { step: null, field: null, group: null };

/** A 0-based inclusive line span inside the authored view text. */
export interface WorkflowViewLineSpan {
  readonly firstLine: number;
  readonly lastLine: number;
}

/**
 * Resolves a structural address to its view-line span: the contiguous run of
 * lines whose meta names the step (and field, when given). `step === null`
 * addresses the header region. Returns null when nothing matches — the caller
 * falls back to the whole step (or nothing).
 */
export function resolveViewSpan(
  lineMeta: readonly WorkflowViewLineMeta[],
  step: string | null,
  field: string | null,
): WorkflowViewLineSpan | null {
  let first = -1;
  let last = -1;
  for (let i = 0; i < lineMeta.length; i++) {
    const meta = lineMeta[i];
    const stepMatches =
      step === null
        ? meta.step === null && meta.group === null && meta.field !== null
        : meta.step !== null && equalsIgnoreCase(meta.step, step);
    const fieldMatches = field === null ? meta.field !== null : meta.field === field;
    if (stepMatches && fieldMatches) {
      if (first < 0) first = i;
      last = i;
    } else if (first >= 0 && field !== null) {
      // Field spans are contiguous; stop at the first break.
      break;
    }
  }

  return first < 0 ? null : { firstLine: first, lastLine: last };
}

// ---------------------------------------------------------------------------
// Rendering (model -> view text)
// ---------------------------------------------------------------------------

export interface WorkflowAuthoredViewRender {
  readonly text: string;
  readonly lineMeta: readonly WorkflowViewLineMeta[];
}

/**
 * Renders the authored view. Deterministic: the same document always renders
 * to the same bytes (canonical field order, one blank line between blocks),
 * which is what keeps the editor's reseed-vs-caret comparison stable.
 */
export function renderAuthoredView(document: WorkflowDocument): WorkflowAuthoredViewRender {
  const lines: string[] = [];
  const meta: WorkflowViewLineMeta[] = [];
  const push = (line: string, m: WorkflowViewLineMeta) => {
    lines.push(line);
    meta.push(m);
  };
  const pushBlank = () => push("", K_BLANK_META);

  push(`${TITLE_PREFIX} ${document.workflowName}`.trimEnd(), {
    step: null,
    field: "name",
    group: null,
  });
  pushBlank();

  // Always rendered, and rendered FIRST: it is the source the title above
  // derives from, so it can never be absent the way an optional field can.
  push(fieldLine(K_NAME_KEY, document.workflowName), {
    step: null,
    field: "name",
    group: null,
  });
  let wroteHeaderField = true;

  if (document.description !== null) {
    push(fieldLine(K_DESCRIPTION_KEY, document.description), {
      step: null,
      field: "description",
      group: null,
    });
    wroteHeaderField = true;
  }

  if (document.expectedDuration !== null) {
    push(fieldLine(K_TAKES_ABOUT_KEY, document.expectedDuration), {
      step: null,
      field: "takes_about",
      group: null,
    });
    wroteHeaderField = true;
  }

  if (document.language !== null) {
    push(fieldLine(K_LANGUAGE_KEY, document.language), {
      step: null,
      field: "language",
      group: null,
    });
    wroteHeaderField = true;
  }

  if (document.product !== null) {
    push(fieldLine(K_PRODUCT_KEY, document.product), {
      step: null,
      field: "product",
      group: null,
    });
    wroteHeaderField = true;
  }

  if (wroteHeaderField) pushBlank();

  // Group headings render positionally: one heading before the first step of
  // each group run. Empty groups render at the end, after every step.
  let openGroup = "";
  for (const step of document.steps) {
    if (
      step.groupName.length > 0 &&
      !equalsIgnoreCase(step.groupName, openGroup)
    ) {
      renderGroupHeading(step.groupName, push, pushBlank);
      openGroup = step.groupName;
    } else if (step.groupName.length === 0) {
      openGroup = "";
    }

    renderStep(step, push, pushBlank);
  }

  for (const group of document.groups) {
    if (group.steps.length === 0) {
      renderGroupHeading(group.name, push, pushBlank);
    }
  }

  // Trim the trailing blank run to exactly zero (join adds no trailing \n).
  while (lines.length > 0 && lines[lines.length - 1].length === 0) {
    lines.pop();
    meta.pop();
  }

  return { text: lines.join("\n"), lineMeta: meta };
}

function renderGroupHeading(
  name: string,
  push: (line: string, m: WorkflowViewLineMeta) => void,
  pushBlank: () => void,
): void {
  push(`## ${GROUP_HEADING_PREFIX} ${name}`.trimEnd(), { step: null, field: null, group: name });
  pushBlank();

  // Always rendered, and rendered FIRST, for the same reason a step's is: the
  // heading above is derived from it.
  push(fieldLine(K_NAME_KEY, name), { step: null, field: "name", group: name });

  pushBlank();
}

function renderStep(
  step: WorkflowStep,
  push: (line: string, m: WorkflowViewLineMeta) => void,
  pushBlank: () => void,
): void {
  const t = step.name;
  const g = step.groupName.length > 0 ? step.groupName : null;
  push(`## ${STEP_HEADING_PREFIX} ${step.name}`, { step: t, field: "name", group: g });
  pushBlank();

  let wroteField = false;
  // A separator owed to whatever comes next, rather than emitted eagerly:
  // `pushBlank` does not dedupe, so the prose block cannot know whether to
  // close itself with a blank until it knows a field follows. Flushed by the
  // first `field` after the block; if none comes, the run's own trailing blank
  // is the separator and this is simply never spent.
  let pendingBlank = false;
  const field = (label: string, value: string | null, key: string) => {
    if (value === null) return;
    if (pendingBlank) {
      pushBlank();
      pendingBlank = false;
    }
    push(fieldLine(label, value), { step: t, field: key, group: g });
    wroteField = true;
  };

  // Always rendered, and rendered FIRST: it is the source the heading above
  // derives from, so it can never be absent the way an optional field can.
  field(K_NAME_KEY, step.name, "name");
  field(K_SCENE_KEY, step.scene, "scene");
  field(K_TAKES_ABOUT_KEY, step.expectedDuration, "takes_about");
  field(K_STEP_CONTEXT_KEY, step.stepContext, "step_context");

  // The prose block sits in the field run, right under the summary it is the
  // counterpart of, and is set off by a blank line on each side so a paragraph
  // or a bulleted list is not read as more rows of the table above it. Blanks
  // only where there is something to separate from, so a step with no
  // instructions renders exactly the contiguous run it always did.
  if (isInstructionsBlockRenderable(step.stepInstructions)) {
    if (wroteField) pushBlank();
    push(`${INSTRUCTIONS_LABEL}:`, { step: t, field: "step_instructions", group: g });
    for (const line of proseBlockLines(step.stepInstructions)) {
      push(line, { step: t, field: "step_instructions", group: g });
    }
    wroteField = true;
    pendingBlank = true;
  }

  field(K_BACK_BUTTON_KEY, step.backButton, "back_button");
  field(K_NEXT_BUTTON_KEY, step.nextButton, "next_button");
  // Not offered EMPTY on an ending: its Next finishes the run, so a target there
  // is an error, and an inviting empty line would be the way in. A value still
  // renders, so that error has a row to point at.
  if (step.nextTarget !== null || (getStepNextButtonLabel(step) !== null && !isCompletionStep(step))) {
    field(K_NEXT_TARGET_KEY, step.nextTarget ?? "", "next_target");
  }
  if (step.attachment !== null) {
    field(K_ATTACHMENT_KEY, formatAttachment(step.attachment), "attachment");
  }
  if (step.media !== null) renderStepMedia(step.media, field);

  // The transcript closes the media run as a BLOCK. Same shape as
  // `Instructions:` above — a blank line before it only when there is
  // something to separate from, and one owed to whatever follows.
  //
  // Offered for a VIDEO, and for anything else only when it already holds
  // something: the video-only fields follow that rule so an image with a stray
  // transcript is a validation warning rather than a value the view silently
  // deletes by not rendering it.
  const media = step.media;
  if (
    media !== null
    && (resolveMediaTypeForView(media) === "video" || media.transcript.length > 0)
    && isTranscriptBlockRenderable(media.transcript)
  ) {
    if (wroteField) pushBlank();
    push(`${MEDIA_TRANSCRIPT_LABEL}:`, { step: t, field: "media_transcript", group: g });
    for (const line of proseBlockLines(media.transcript)) {
      push(line, { step: t, field: "media_transcript", group: g });
    }
    wroteField = true;
  }

  if (wroteField) pushBlank();

  const authoredScript = findAuthoredScript(step.scripts);
  if (
    authoredScript !== null
    && authoredScript.steps.length > 0
    && isCommandBlockRenderable(authoredScript)
  ) {
    push(`${COMMANDS_LABEL}:`, { step: t, field: "commands", group: g });
    for (const command of authoredScript.steps) {
      // `formatCommandEntry` only returns null for an entry the block gate
      // above already excluded, so the fallback is unreachable in practice.
      push(formatCommandEntry(command) ?? "", { step: t, field: "commands", group: g });
    }
    pushBlank();
  }

  if (step.triggers.length > 0) {
    push(`${TRIGGERS_LABEL}:`, { step: t, field: "triggers", group: g });
    for (const trigger of step.triggers) {
      push(formatTriggerEntry(trigger), { step: t, field: "triggers", group: g });
    }
    pushBlank();
  }
}

/**
 * Whether a step's `step_instructions` can be projected into the authored view
 * without being read back as something else.
 *
 * The view is a prefix-sensitive line grammar, so two of its own constructs
 * collide with Markdown and one of them is destructive:
 *
 *   - A `#` HEADING line splits sections. `parseAuthoredView` opens a new step
 *     region at every `##`, so a heading inside the prose would saw the step in
 *     two and the half below it would become a step of its own. There is
 *     nothing to escape it with, and nothing to head anyway — the step's name
 *     is already the panel's heading.
 *   - A line shaped like one of the view's own LABELS (`Back button:`,
 *     `Commands:`, a retired `On Start:`) closes the block, so the prose after
 *     it would be dropped. Note this is the CLOSED list in
 *     {@link isAnyKnownLabel}, not "any `Word:` line" — `Note: be careful` is
 *     ordinary prose and stays.
 *
 * Rather than silently truncating either, the block is not rendered at all and
 * the value is carried across the edit ({@link carryMachineData}) — the same
 * rule an unreadable command entry follows ({@link isCommandBlockRenderable}).
 * The instructions still play, still reach the agent, and are still editable in
 * raw YAML; they are simply not in the WYSIWYG projection.
 *
 * ⚠ This used to claim `validate()` says so, and it does not — see the note on
 * {@link isTranscriptBlockRenderable} for why it cannot.
 *
 * `null` (the field is absent) is renderable-as-nothing, which is why the
 * caller's own null check is what decides whether a label appears at all.
 */
export function isInstructionsBlockRenderable(value: string | null): boolean {
  if (value === null) return false;
  for (const line of value.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    if (K_HEADING_REGEX.test(trimmed)) return false;
    if (isAnyKnownLabel(trimmed)) return false;
  }
  return true;
}

/**
 * Whether a step's `media.transcript` can be projected into the authored view
 * without being read back as something else.
 *
 * Exactly the rule {@link isInstructionsBlockRenderable} applies, for exactly
 * the same two reserved constructs — a `#` heading saws the step in two, and a
 * line shaped like one of the view's own labels closes the block — so the two
 * share an implementation rather than being written twice. A stored value
 * containing either is not rendered and is carried across the edit
 * ({@link carryMachineData}); it still plays, still reaches the agent, and is
 * still editable in raw YAML.
 *
 * ⚠ Nothing WARNS about it. `validate()` would have to call this predicate,
 * which needs the label list above, and `WorkflowDocument` importing this file
 * is a cycle (it is imported BY this one). The agent-facing surfaces — the
 * published schema and the file spec — say what is reserved instead, which is
 * what keeps it rare.
 *
 * ⚠ The reserved list is the CLOSED one in {@link isAnyKnownLabel}, not "any
 * `Word:` line" — which matters more here than it does for instructions,
 * because a transcript is speech and speech gets written with names in front
 * of it. `Alex: and then we release the clips` is ordinary prose and stays;
 * only `Name:`, `Scene:`, `Media url:` and their siblings are structure.
 */
export function isTranscriptBlockRenderable(value: string): boolean {
  return isInstructionsBlockRenderable(value);
}

/**
 * The lines of a prose BLOCK (`Instructions:`, `Media transcript:`) as the view
 * emits them.
 *
 * Trailing blank lines go: the block is closed by a blank in the rendered view
 * and the parse trims them back, so keeping one in the value would grow the
 * document by a line on every round trip. Interior blanks are the paragraph
 * breaks and are kept exactly.
 */
function proseBlockLines(value: string | null): string[] {
  if (value === null) return [];
  const lines = value.split("\n").map((line) => line.trimEnd());
  while (lines.length > 0 && lines[lines.length - 1].length === 0) lines.pop();
  while (lines.length > 0 && lines[0].length === 0) lines.shift();
  return lines;
}

/**
 * Renders a step's media block.
 *
 * `Media type:` renders what the author STATED, so an empty line is what an
 * inferred type looks like — the field's hint says so. Rendering the inferred
 * kind instead would promote it to an explicit key the moment anyone touched
 * the step in the editor, which breaks the render -> parse -> serialize
 * byte-stability the whole projection rests on.
 *
 * The video-only fields render for a video, and for an image only when they
 * already hold something: an image with a stray transcript is a validation
 * warning, and a view that did not render it would DELETE it on parse-back.
 *
 * The transcript is NOT one of them: it is a block, so the caller emits it
 * after this run (see {@link MEDIA_TRANSCRIPT_LABEL}).
 */
function renderStepMedia(
  media: WorkflowStepMedia,
  field: (label: string, value: string | null, key: string) => void,
): void {
  const isVideo = resolveMediaTypeForView(media) === "video";
  field(
    K_MEDIA_TYPE_KEY,
    media.invalidType.length > 0 ? media.invalidType : (media.type ?? ""),
    "media_type",
  );
  // `fit` renders ALWAYS, like `type` and unlike the video-only three: it is a
  // decision about any media, and an empty line with its hint is the only way
  // an author can make it from the editor at all.
  field(K_MEDIA_FIT_KEY, media.invalidFit.length > 0 ? media.invalidFit : (media.fit ?? ""), "media_fit");
  field(K_MEDIA_URL_KEY, media.url, "media_url");
  field(K_MEDIA_NAME_KEY, media.name, "media_name");
  field(K_MEDIA_DESCRIPTION_KEY, media.description, "media_description");
  if (isVideo || media.startTime.length > 0) {
    field(K_MEDIA_START_KEY, media.startTime, "media_start");
  }
  if (isVideo || media.endTime.length > 0) {
    field(K_MEDIA_END_KEY, media.endTime, "media_end");
  }
  // The file's OWN words (`true` / `false`), not an invented pair: the view is
  // a projection of the YAML, and a reader moving between the two surfaces
  // should see one vocabulary. The picker's menu carries the readable phrasing.
  if (isVideo || media.loop !== null) {
    field(K_MEDIA_LOOP_KEY, media.loop === null ? "" : String(media.loop), "media_loop");
  }
  if (isVideo || media.autoplay !== null) {
    field(
      K_MEDIA_AUTOPLAY_KEY,
      media.autoplay === null ? "" : String(media.autoplay),
      "media_autoplay",
    );
  }
}

/**
 * Whether to OFFER the video-only fields for an untyped URL. Deliberately not
 * `resolveMediaType` — that one answers "what does the player do", this one
 * only decides which labels appear, and the two must not be able to make the
 * view depend on the player's fallback.
 */
function resolveMediaTypeForView(media: WorkflowStepMedia): "image" | "video" {
  return media.type ?? inferMediaTypeForView(media.url);
}

function inferMediaTypeForView(url: string): "image" | "video" {
  return /\.(mp4|m4v|webm|ogv|mov|mpd|m3u8)(?:[?#]|$)/i.test(url.trim()) ? "video" : "image";
}

/**
 * A step attachment as its one-line authored value: the part path, plus the
 * exact point when one was captured. Kept on ONE line because every other
 * authored field is `Key: value` — a nested block here would be the only place
 * the grammar indents, for a value the author reads far more often than edits.
 */
function formatAttachment(attachment: WorkflowStepAttachment): string {
  const point = attachment.point;
  if (point === null) return attachment.part;
  const coords = [point.x, point.y, point.z].map((n) => round3(n)).join(", ");
  return attachment.part.length > 0 ? `${attachment.part} @ ${coords}` : `@ ${coords}`;
}

/** Inverse of {@link formatAttachment}; null when the value names nothing. */
function parseAttachmentValue(value: string): WorkflowStepAttachment | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  const attachment = new WorkflowStepAttachment();
  const at = trimmed.lastIndexOf("@");
  if (at < 0) {
    attachment.part = trimmed;
    return attachment;
  }

  attachment.part = trimmed.slice(0, at).trim();
  const coords = trimmed
    .slice(at + 1)
    .split(",")
    .map((part) => Number(part.trim()));
  if (coords.length === 3 && coords.every((n) => Number.isFinite(n))) {
    attachment.point = { x: coords[0], y: coords[1], z: coords[2] };
  } else {
    // Not a point after all — keep the whole thing as the part name rather
    // than dropping what the author typed.
    attachment.part = trimmed;
  }
  return attachment.isEmpty ? null : attachment;
}

/** Three decimals: millimetre precision, and no float noise in the view text. */
function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function fieldLine(label: string, value: string): string {
  return value.length === 0 ? `${label}:` : `${label}: ${value}`;
}

/** One trigger as its authored line body (after the `N. ` number). */
function formatTriggerEntry(trigger: WorkflowStepTrigger): string {
  // An unparseable line round-trips verbatim so a rewrite cannot delete it.
  if (trigger.invalidText.length > 0) return trigger.invalidText;
  if (trigger.isInvalid) return String(trigger.invalidRaw ?? "");

  const typeWord = trigger.invalidType.length > 0 ? trigger.invalidType : trigger.type;
  return `${capitalizeFirst(typeWord)} "${trigger.label}" -> ${trigger.goTo}`;
}

function capitalizeFirst(value: string): string {
  return value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);
}

// ---------------------------------------------------------------------------
// Parsing (view text -> authored content)
// ---------------------------------------------------------------------------

export interface WorkflowAuthoredViewParse {
  /** The parsed authored content as a document (no machine data: no ids, no scripts). */
  readonly authored: WorkflowDocument;
  /** Per-line structural addresses for the EXACT text that was parsed. */
  readonly lineMeta: readonly WorkflowViewLineMeta[];
}

/**
 * Parses an authored view text. Total: any input produces a document. Lines
 * the grammar does not model are dropped; structural labels are locked so
 * that is rare, and retired keys still terminate a wrapped field.
 */
export function parseAuthoredView(text: string): WorkflowAuthoredViewParse {
  const normalized = text
    .replace(/^\uFEFF/, "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
  const lines = normalized.length === 0 ? [] : normalized.split("\n");
  const meta: WorkflowViewLineMeta[] = lines.map(() => K_BLANK_META);

  const document = new WorkflowDocument();

  // Split into regions: header (before the first ## heading), then group /
  // step sections in order.
  type Region =
    | { kind: "header"; start: number; end: number }
    | { kind: "group"; name: string; headingLine: number; start: number; end: number }
    | { kind: "step"; name: string; headingLine: number; start: number; end: number };

  const regions: Region[] = [];
  let cursor = 0;
  let currentStart = 0;
  let current: Region | null = { kind: "header", start: 0, end: lines.length };

  const closeCurrent = (end: number) => {
    if (current !== null) {
      current.end = end;
      regions.push(current);
      current = null;
    }
  };

  for (cursor = 0; cursor < lines.length; cursor++) {
    const heading = K_HEADING_REGEX.exec(lines[cursor]);
    if (heading === null || heading[1].length !== 2) continue;

    const headingText = heading[2].trim();
    closeCurrent(cursor);
    currentStart = cursor + 1;

    if (startsWithIgnoreCase(headingText, GROUP_HEADING_PREFIX)) {
      current = {
        kind: "group",
        name: headingText.slice(GROUP_HEADING_PREFIX.length).trim(),
        headingLine: cursor,
        start: currentStart,
        end: lines.length,
      };
    } else if (startsWithIgnoreCase(headingText, STEP_HEADING_PREFIX)) {
      current = {
        kind: "step",
        name: headingText.slice(STEP_HEADING_PREFIX.length).trim(),
        headingLine: cursor,
        start: currentStart,
        end: lines.length,
      };
    } else {
      // An unknown `## heading` still opens a step region so the parse stays
      // total; its body is parsed as a step named after the heading text.
      current = {
        kind: "step",
        name: headingText,
        headingLine: cursor,
        start: currentStart,
        end: lines.length,
      };
    }
  }
  closeCurrent(lines.length);

  let openGroup = "";
  for (const region of regions) {
    if (region.kind === "header") {
      parseHeaderRegion(document, lines, meta, region.start, region.end);
    } else if (region.kind === "group") {
      const group = new WorkflowGroup();
      // The heading is only a fallback, exactly like a step's: `Name:` in the
      // body is where a group is authored, and `parseGroupRegion` overwrites
      // this when it finds one. The fallback still matters — mid-edit the field
      // is momentarily blank, and a group must never lose its identity (which
      // is also what its steps reference) between two keystrokes.
      group.name = region.name;
      parseGroupRegion(group, lines, meta, region.start, region.end);
      // Recorded AFTER the body parse so the heading's metadata names the group
      // the way the rest of the section does.
      meta[region.headingLine] = { step: null, field: null, group: group.name };
      document.groups.push(group);
      // Steps below this heading take the group's FINAL name, so renaming a
      // group in its field carries its membership with it.
      openGroup = group.name;
    } else {
      const step = new WorkflowStep();
      // The heading is only a fallback: `Name:` in the body is the authored
      // source, and `parseStepRegion` overwrites this when it finds one. The
      // fallback still matters — mid-edit the field can be momentarily blank,
      // and a step must never lose its identity between two keystrokes.
      step.name = region.name;
      step.groupName = openGroup;
      parseStepRegion(step, lines, meta, region.start, region.end);
      // Recorded AFTER the body parse so the heading's metadata names the step
      // the way the rest of the section does.
      meta[region.headingLine] = {
        step: step.name,
        field: "name",
        group: openGroup.length > 0 ? openGroup : null,
      };
      document.steps.push(step);
    }
  }

  rebuildAuthoredGroups(document);
  return { authored: document, lineMeta: meta };
}

function rebuildAuthoredGroups(document: WorkflowDocument): void {
  // Membership from step order; keep explicitly-declared (possibly empty)
  // groups.
  const byName = new Map<string, WorkflowGroup>();
  for (const group of document.groups) byName.set(group.name.toLowerCase(), group);
  for (const step of document.steps) {
    if (step.groupName.length === 0) continue;
    const existing = byName.get(step.groupName.toLowerCase());
    if (existing) {
      step.groupName = existing.name;
      existing.steps.push(step);
    }
  }
}

function parseHeaderRegion(
  document: WorkflowDocument,
  lines: string[],
  meta: WorkflowViewLineMeta[],
  start: number,
  end: number,
): void {
  for (let i = start; i < end; i++) {
    const line = lines[i].trim();
    if (line.length === 0) continue;

    if (startsWithIgnoreCase(line, TITLE_PREFIX)) {
      document.workflowName = line.slice(TITLE_PREFIX.length).trim();
      meta[i] = { step: null, field: "name", group: null };
      continue;
    }

    if (line.startsWith("# ") && document.workflowName.length === 0) {
      document.workflowName = line.slice(2).trim();
      meta[i] = { step: null, field: "name", group: null };
      continue;
    }

    const name = tryGetFieldValue(line, K_NAME_KEY);
    if (name !== null) {
      // A blank `Name:` keeps the title-derived fallback: mid-edit the field is
      // empty for a keystroke, and the workflow must not lose its name between
      // two of them. Same rule as a step's.
      if (name.length > 0) document.workflowName = name;
      meta[i] = { step: null, field: "name", group: null };
      continue;
    }

    const description = tryGetFieldValue(line, K_DESCRIPTION_KEY);
    if (description !== null) {
      document.description = description;
      meta[i] = { step: null, field: "description", group: null };
      continue;
    }

    const duration = tryGetFieldValue(line, K_TAKES_ABOUT_KEY);
    if (duration !== null) {
      document.expectedDuration = duration;
      meta[i] = { step: null, field: "takes_about", group: null };
      continue;
    }

    const language = tryGetFieldValue(line, K_LANGUAGE_KEY);
    if (language !== null) {
      document.language = language;
      meta[i] = { step: null, field: "language", group: null };
      continue;
    }

    const product = tryGetFieldValue(line, K_PRODUCT_KEY);
    if (product !== null) {
      document.product = product;
      meta[i] = { step: null, field: "product", group: null };
      continue;
    }
  }
}

/**
 * A group's body: its `Name:` field. Unmodelled lines are dropped.
 */
function parseGroupRegion(
  group: WorkflowGroup,
  lines: string[],
  meta: WorkflowViewLineMeta[],
  start: number,
  end: number,
): void {
  // Resolve the name BEFORE the main pass: every line's metadata is addressed
  // by group name, so reading `Name:` in document order would leave the lines
  // above it labelled with the heading's stale name for one keystroke.
  for (let i = start; i < end; i++) {
    const value = tryGetFieldValue(lines[i].trim(), K_NAME_KEY);
    if (value !== null && value.length > 0) {
      group.name = value;
      break;
    }
  }

  for (let i = start; i < end; i++) {
    if (lines[i].trim().length === 0) continue;

    if (tryGetFieldValue(lines[i].trim(), K_NAME_KEY) !== null) {
      meta[i] = { step: null, field: "name", group: group.name };
    }
  }
}

function parseStepRegion(
  step: WorkflowStep,
  lines: string[],
  meta: WorkflowViewLineMeta[],
  start: number,
  end: number,
): void {
  // Resolve the name BEFORE the main pass: every line's metadata is addressed
  // by step name, so reading `Name:` in document order would leave the lines
  // above it labelled with the heading's stale name for one keystroke.
  for (let i = start; i < end; i++) {
    const value = tryGetFieldValue(lines[i].trim(), K_NAME_KEY);
    if (value !== null && value.length > 0) {
      step.name = value;
      break;
    }
  }

  const t = step.name;
  const g = step.groupName.length > 0 ? step.groupName : null;

  let i = start;
  while (i < end) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed.length === 0) {
      i++;
      continue;
    }

    if (K_TRIGGER_LABEL_REGEX.test(trimmed)) {
      // Retired `On Start:` / `On Button "…":` labels — skip the label and
      // any numbered lines under it so they cannot fold into a field above.
      i++;
      while (i < end && K_STEP_LINE_REGEX.test(lines[i])) i++;
      continue;
    }

    if (isLabelLine(trimmed, INSTRUCTIONS_LABEL)) {
      i = parseInstructions(step, lines, meta, i, end, t, g);
      continue;
    }

    if (isLabelLine(trimmed, MEDIA_TRANSCRIPT_LABEL)) {
      i = parseTranscript(step, lines, meta, i, end, t, g);
      continue;
    }

    if (isLabelLine(trimmed, COMMANDS_LABEL)) {
      i = parseCommands(step, lines, meta, i, end, t, g);
      continue;
    }

    if (isLabelLine(trimmed, TRIGGERS_LABEL)) {
      i = parseTriggers(step, lines, meta, i, end, t, g);
      continue;
    }

    let matchedField = false;
    for (const key of K_STEP_FIELD_KEYS) {
      const value = tryGetFieldValue(trimmed, key);
      if (value === null) continue;

      const continuation = appendFieldContinuations(lines, i, end, value);
      for (let j = i; j < continuation.nextIndex; j++) {
        meta[j] = { step: t, field: fieldKeyOf(key), group: g };
      }
      applyStepField(step, key, continuation.value);
      i = continuation.nextIndex;
      matchedField = true;
      break;
    }

    if (!matchedField) i++;
  }

  // Every media line came back blank: the author emptied the block, so the
  // block is gone rather than serializing as `media: {}`.
  if (step.media !== null && step.media.isEmpty) step.media = null;
}

function fieldKeyOf(label: string): string {
  switch (label) {
    case K_NAME_KEY:
      return "name";
    case K_SCENE_KEY:
      return "scene";
    case K_TAKES_ABOUT_KEY:
      return "takes_about";
    case K_DESCRIPTION_KEY:
      return "description";
    case K_STEP_CONTEXT_KEY:
      return "step_context";
    case K_INSTRUCTIONS_KEY:
      return "step_instructions";
    case K_BACK_BUTTON_KEY:
      return "back_button";
    case K_NEXT_BUTTON_KEY:
      return "next_button";
    case K_NEXT_TARGET_KEY:
      return "next_target";
    case K_ATTACHMENT_KEY:
      return "attachment";
    case K_MEDIA_TYPE_KEY:
      return "media_type";
    case K_MEDIA_FIT_KEY:
      return "media_fit";
    case K_MEDIA_URL_KEY:
      return "media_url";
    case K_MEDIA_NAME_KEY:
      return "media_name";
    case K_MEDIA_DESCRIPTION_KEY:
      return "media_description";
    case K_MEDIA_START_KEY:
      return "media_start";
    case K_MEDIA_END_KEY:
      return "media_end";
    case K_MEDIA_LOOP_KEY:
      return "media_loop";
    case K_MEDIA_AUTOPLAY_KEY:
      return "media_autoplay";
    default:
      return label.toLowerCase();
  }
}

/**
 * The step's media, created on first touch. A media BLOCK exists as soon as any
 * of its lines does, and `parseStepRegion` drops it again when every line came
 * back empty — otherwise deleting the last media line would leave an empty
 * `media: {}` in the file.
 */
function ensureMedia(step: WorkflowStep): WorkflowStepMedia {
  if (step.media === null) step.media = new WorkflowStepMedia();
  return step.media;
}

function applyMediaField(step: WorkflowStep, key: string, value: string): boolean {
  if (equalsIgnoreCase(key, K_MEDIA_TYPE_KEY)) {
    const media = ensureMedia(step);
    const normalized = value.length === 0 ? null : normalizeMediaType(value);
    media.type = normalized;
    media.invalidType = value.length > 0 && normalized === null ? value : "";
  } else if (equalsIgnoreCase(key, K_MEDIA_FIT_KEY)) {
    const media = ensureMedia(step);
    const normalized = value.length === 0 ? null : normalizeMediaFit(value);
    media.fit = normalized;
    media.invalidFit = value.length > 0 && normalized === null ? value : "";
  } else if (equalsIgnoreCase(key, K_MEDIA_URL_KEY)) ensureMedia(step).url = value;
  else if (equalsIgnoreCase(key, K_MEDIA_NAME_KEY)) ensureMedia(step).name = value;
  else if (equalsIgnoreCase(key, K_MEDIA_DESCRIPTION_KEY)) ensureMedia(step).description = value;
  else if (equalsIgnoreCase(key, K_MEDIA_START_KEY)) ensureMedia(step).startTime = value;
  else if (equalsIgnoreCase(key, K_MEDIA_END_KEY)) ensureMedia(step).endTime = value;
  else if (equalsIgnoreCase(key, K_MEDIA_LOOP_KEY)) ensureMedia(step).loop = parseOptionalBooleanWord(value);
  else if (equalsIgnoreCase(key, K_MEDIA_AUTOPLAY_KEY)) ensureMedia(step).autoplay = parseOptionalBooleanWord(value);
  else return false;
  return true;
}

/**
 * A media boolean field's value as the model's tri-state. The view renders the
 * file's own `true` / `false`, so those are the two words read back; empty is
 * the field left unstated. Used by both `Media loop:` and `Media autoplay:`.
 *
 * Anything else reads as unstated, which is unreachable in practice: the field
 * carries a PICKER, and a chosen value is menu-only — it cannot be typed into
 * and the caret never lands inside it. A word this does not know can therefore
 * only come from raw YAML, where the codec refuses a non-boolean outright.
 */
function parseOptionalBooleanWord(value: string): boolean | null {
  const word = value.trim().toLowerCase();
  if (word === "true") return true;
  if (word === "false") return false;
  return null;
}

function applyStepField(step: WorkflowStep, key: string, value: string): void {
  // `Name` was already resolved by the pre-scan; re-applying a blank one here
  // would wipe the fallback the heading provided.
  if (equalsIgnoreCase(key, K_NAME_KEY)) {
    if (value.length > 0) step.name = value;
  } else if (equalsIgnoreCase(key, K_SCENE_KEY)) step.scene = value;
  else if (equalsIgnoreCase(key, K_TAKES_ABOUT_KEY)) step.expectedDuration = value;
  else if (equalsIgnoreCase(key, K_STEP_CONTEXT_KEY)) step.stepContext = value;
  else if (equalsIgnoreCase(key, K_BACK_BUTTON_KEY)) step.backButton = value;
  else if (equalsIgnoreCase(key, K_NEXT_BUTTON_KEY)) step.nextButton = value;
  else if (equalsIgnoreCase(key, K_NEXT_TARGET_KEY)) step.nextTarget = normalizeNextTarget(value);
  else if (equalsIgnoreCase(key, K_ATTACHMENT_KEY)) step.attachment = parseAttachmentValue(value);
  else applyMediaField(step, key, value);
}

/**
 * Appends wrapped continuation lines to a "Key: value" field. A continuation
 * line is any non-blank line that is not itself a field, label, bullet, step,
 * or trigger line.
 */
function appendFieldContinuations(
  lines: string[],
  fieldLineIndex: number,
  endExclusive: number,
  value: string,
): { nextIndex: number; value: string } {
  let i = fieldLineIndex + 1;
  while (i < endExclusive) {
    const trimmed = lines[i].trim();
    if (
      trimmed.length === 0 ||
      isAnyKnownLabel(trimmed) ||
      trimmed.startsWith("-") ||
      K_STEP_LINE_REGEX.test(trimmed) ||
      K_TRIGGER_LABEL_REGEX.test(trimmed)
    ) {
      break;
    }

    value = `${value} ${trimmed}`.trim();
    i++;
  }

  return { nextIndex: i, value };
}

/**
 * Reads the `Instructions:` block — the step's learner-facing prose.
 *
 * Unlike {@link appendFieldContinuations}, which is what an inline field uses,
 * this keeps LINE BREAKS and does not stop at a blank line or a `-`: those are
 * the paragraph breaks and the bullets the markup is made of. It ends only
 * where the next STRUCTURE begins — a heading, or one of the view's own labels
 * — which is exactly what {@link isInstructionsBlockRenderable} refuses to emit
 * into the block for that reason.
 *
 * The label may carry an inline remainder (`Instructions: do the thing`), which
 * the renderer never emits but an author can type, and which is also what every
 * document written before the block existed looks like. Taking it as the first
 * line means such a line reads correctly and is re-rendered in block form on
 * the next round trip, rather than being silently dropped.
 *
 * An empty block is an empty STRING, not null: the label is on screen, so the
 * field is present and the panel shows nothing — the same "emptiness is the
 * hidden state" rule the field has always had. Only an absent label leaves the
 * value null.
 */
function parseInstructions(
  step: WorkflowStep,
  lines: string[],
  meta: WorkflowViewLineMeta[],
  labelLineIndex: number,
  endExclusive: number,
  t: string,
  g: string | null,
): number {
  meta[labelLineIndex] = { step: t, field: "step_instructions", group: g };

  const collected: string[] = [];
  const inline = tryGetFieldValue(lines[labelLineIndex].trim(), INSTRUCTIONS_LABEL);
  if (inline !== null && inline.length > 0) collected.push(inline);

  let i = labelLineIndex + 1;
  while (i < endExclusive) {
    const trimmed = lines[i].trim();
    if (trimmed.length > 0 && (K_HEADING_REGEX.test(trimmed) || isAnyKnownLabel(trimmed))) break;
    collected.push(lines[i].trimEnd());
    meta[i] = { step: t, field: "step_instructions", group: g };
    i++;
  }

  while (collected.length > 0 && collected[collected.length - 1].length === 0) collected.pop();
  step.stepInstructions = collected.join("\n");
  return i;
}

/**
 * Reads the `Commands:` block into a fresh on-start script.
 *
 * The script is a CONTAINER here — its id, name and revert mode are machine
 * data that `carryMachineData` restores from the base document; only the
 * ordered command list is authored.
 */
/**
 * Reads the `Media transcript:` block — the step's video transcript.
 *
 * The prose half of {@link parseInstructions}, and it keeps LINE BREAKS for the
 * same reason: a blank line is a paragraph break in a transcript, not the end
 * of the value, and stopping at one is what used to throw everything after the
 * first paragraph away. It ends only where the next STRUCTURE begins — a
 * heading, or one of the view's own labels.
 *
 * The label may carry an inline remainder (`Media transcript: one line`), which
 * the renderer never emits but an author can type — and which is also what
 * every document written before the block existed looks like, since the field
 * was inline until 2026-09-02. Taking it as the first line means such a line
 * reads correctly and is re-rendered in block form on the next round trip,
 * rather than being dropped.
 *
 * Unlike instructions, an empty block is an empty STRING on a media block that
 * already exists rather than a `null` field: `media.transcript` is a plain
 * string, and `ensureMedia` is what decides whether the step has media at all.
 */
function parseTranscript(
  step: WorkflowStep,
  lines: string[],
  meta: WorkflowViewLineMeta[],
  labelLineIndex: number,
  endExclusive: number,
  t: string,
  g: string | null,
): number {
  meta[labelLineIndex] = { step: t, field: "media_transcript", group: g };

  const collected: string[] = [];
  const inline = tryGetFieldValue(lines[labelLineIndex].trim(), MEDIA_TRANSCRIPT_LABEL);
  if (inline !== null && inline.length > 0) collected.push(inline);

  let i = labelLineIndex + 1;
  while (i < endExclusive) {
    const trimmed = lines[i].trim();
    if (trimmed.length > 0 && (K_HEADING_REGEX.test(trimmed) || isAnyKnownLabel(trimmed))) break;
    collected.push(lines[i].trimEnd());
    meta[i] = { step: t, field: "media_transcript", group: g };
    i++;
  }

  while (collected.length > 0 && collected[collected.length - 1].length === 0) collected.pop();
  ensureMedia(step).transcript = collected.join("\n");
  return i;
}

function parseCommands(
  step: WorkflowStep,
  lines: string[],
  meta: WorkflowViewLineMeta[],
  labelLineIndex: number,
  endExclusive: number,
  t: string,
  g: string | null,
): number {
  meta[labelLineIndex] = { step: t, field: "commands", group: g };

  const script = new WorkflowScript();
  script.trigger = "on_start";

  let i = labelLineIndex + 1;
  while (i < endExclusive) {
    const entry = readBlockEntry(lines, i);
    if (entry === null) break;

    // A line with no command yet — `1. ` or `1. say ` while the author is
    // still typing — is not written. It would serialize as `{ tool: "" }`,
    // which the reader REFUSES, so a half-typed line would make the whole file
    // unreadable on the next save.
    const command = parseCommandEntry(entry);
    if (command.tool.length > 0 || command.sayText.trim().length > 0) {
      script.steps.push(command);
    }
    meta[i] = { step: t, field: "commands", group: g };
    i++;
  }

  // An empty block means the author deleted every line; the carry treats that
  // as "no commands" rather than restoring the base's, which is what makes a
  // deletion in the editor actually delete.
  step.scripts.push(script);
  return i;
}

/**
 * One entry line of a `Commands:` / `Triggers:` block, or null at its end.
 *
 * Entries carry NO marker — the block label groups them, and the numbering they
 * used to have was noise the author could also type into and break. So the
 * block ends where any other structure begins: a blank line, another label, or
 * a heading. A legacy `N. ` prefix is stripped so a file written by the earlier
 * grammar still reads.
 */
function readBlockEntry(lines: string[], index: number): string | null {
  const trimmed = lines[index].trim();
  if (trimmed.length === 0) return null;
  if (isAnyKnownLabel(trimmed)) return null;
  if (K_HEADING_REGEX.test(trimmed)) return null;

  const legacy = K_STEP_LINE_REGEX.exec(trimmed);
  return legacy === null ? trimmed : legacy[2].trim();
}

function parseTriggers(
  step: WorkflowStep,
  lines: string[],
  meta: WorkflowViewLineMeta[],
  labelLineIndex: number,
  endExclusive: number,
  t: string,
  g: string | null,
): number {
  meta[labelLineIndex] = { step: t, field: "triggers", group: g };
  let i = labelLineIndex + 1;
  while (i < endExclusive) {
    const entry = readBlockEntry(lines, i);
    if (entry === null) break;

    step.triggers.push(parseTriggerEntry(entry));
    meta[i] = { step: t, field: "triggers", group: g };
    i++;
  }

  return i;
}

/** Maps one authored trigger entry (the text after `N. `) onto a trigger. */
function parseTriggerEntry(text: string): WorkflowStepTrigger {
  const trigger = new WorkflowStepTrigger();
  const match = K_TRIGGER_BUTTON_REGEX.exec(text);
  if (match !== null) {
    trigger.type = "button";
    trigger.label = match[1].trim();
    trigger.goTo = trimChars(match[2].trim(), "\"'");
    return trigger;
  }

  // A recognisable `<Word> "<label>" -> <target>` shape with an unknown type
  // word is reported as a bad TYPE (naming the word); anything else is
  // reported as an unparseable line.
  const typed = /^([A-Za-z_][\w-]*)\s+"([^"]*)"\s*(?:->|→)\s*(.+)$/.exec(text);
  if (typed !== null) {
    trigger.invalidType = typed[1];
    trigger.label = typed[2].trim();
    trigger.goTo = trimChars(typed[3].trim(), "\"'");
    return trigger;
  }

  trigger.invalidText = text;
  return trigger;
}

function isAnyKnownLabel(trimmedLine: string): boolean {
  if (isLabelLine(trimmedLine, TRIGGERS_LABEL)) return true;
  if (isLabelLine(trimmedLine, COMMANDS_LABEL)) return true;
  if (K_TRIGGER_LABEL_REGEX.test(trimmedLine)) return true;

  for (const key of K_STEP_LABEL_KEYS) {
    if (tryGetFieldValue(trimmedLine, key) !== null) return true;
  }

  // Retired keys still terminate a wrapped field so a legacy line is never
  // folded into the value above it (see K_RETIRED_FIELD_KEYS).
  for (const key of K_RETIRED_FIELD_KEYS) {
    if (tryGetFieldValue(trimmedLine, key) !== null) return true;
  }

  return false;
}

function isLabelLine(trimmedLine: string, label: string): boolean {
  return startsWithIgnoreCase(trimmedLine, label + ":");
}

function tryGetFieldValue(trimmedLine: string, key: string): string | null {
  const prefix = key + ":";
  if (!startsWithIgnoreCase(trimmedLine, prefix)) return null;
  return trimmedLine.slice(prefix.length).trim();
}

function startsWithIgnoreCase(value: string, prefix: string): boolean {
  return value.toLowerCase().startsWith(prefix.toLowerCase());
}

// ---------------------------------------------------------------------------
// Applying an edited view back onto a document
// ---------------------------------------------------------------------------

/**
 * Builds the new document an edited view text describes: the authored layer
 * comes wholesale from the parsed text; identity, run state, step ids and
 * prepared scripts are carried over from `base`.
 *
 * Machine-data carry-over matches steps by NAME first (case-insensitive).
 * Steps left unmatched on both sides are then paired POSITIONALLY in order,
 * so a step renamed in the editor keeps its `stepId` and `scripts` instead
 * of orphaning them. Scripts of a step that was genuinely deleted are
 * dropped with the step — deleting a step in the editor deletes it.
 */
export function applyAuthoredView(
  base: WorkflowDocument,
  editedText: string,
): WorkflowDocument {
  const { authored } = parseAuthoredView(editedText);

  authored.uid = base.uid;
  authored.type = base.type;
  authored.version = base.version;
  authored.knowledgeDocs = base.knowledgeDocs;
  authored.projectId = base.projectId;
  authored.projectName = base.projectName;
  // The header BOOLEANS have no line in this grammar (raw-YAML / agent-owned),
  // so the parse can only ever produce their absent value — they have to be
  // carried, or every WYSIWYG save silently clears them.
  authored.userInputAllowed =
    base.userInputAllowed === null ? null : [...base.userInputAllowed];
  authored.invalidUserInputAllowed = [...base.invalidUserInputAllowed];
  authored.autostartDesktop = base.autostartDesktop;
  authored.autostartHeadset = base.autostartHeadset;
  authored.rememberProgress = base.rememberProgress;
  authored.requiresConsolePlacement = base.requiresConsolePlacement;
  authored.lockUi = base.lockUi;
  authored.useCustomUi = base.useCustomUi;
  authored.disableMobileKeyboard = base.disableMobileKeyboard;

  /*
    `workflow_ui` is a top-level MACHINE-OWNED section with no line in this
    grammar either, so it needs carrying for the same reason as the booleans
    above — but it is not a header key, and the comment above scopes itself to
    those, so it gets its own block rather than being appended to that list.

    ⚠ CLONED, not referenced. The two are both used a few lines up
    (`knowledgeDocs` is carried by reference, `userInputAllowed` is copied), and
    the difference matters here: a LIVE panel writes this section, so sharing
    the array would let a drag mid-save mutate a document the rest of the
    codebase treats as an immutable snapshot.

    The history that makes this row non-optional: `applyAuthoredView` was
    silently clearing `autostart_*` and `allow_user_input` on every WYSIWYG save
    for weeks, because the change that added them recorded row 3 as "needed no
    change". It is negative-controlled in the harness.
  */
  authored.workflowUi = cloneWorkflowUiForCarry(base.workflowUi);

  const matchedOld = new Set<WorkflowStep>();
  const unmatchedNew: WorkflowStep[] = [];

  for (const step of authored.steps) {
    const byName = base.findStep(step.name);
    if (byName !== null && !matchedOld.has(byName)) {
      carryMachineData(step, byName);
      matchedOld.add(byName);
    } else {
      unmatchedNew.push(step);
    }
  }

  const unmatchedOld = base.steps.filter((step) => !matchedOld.has(step));
  for (let i = 0; i < unmatchedNew.length && i < unmatchedOld.length; i++) {
    carryMachineData(unmatchedNew[i], unmatchedOld[i]);
  }

  return authored;
}

/**
 * Combines the authored command list with the scripts' machine data.
 *
 * The `Commands:` block authors ONE thing: the ordered steps of the on-start
 * script. Everything else about that script — its id, name and revert mode —
 * and every other script the step has are machine data, restored from `source`
 * here the way `stepId` and the unrendered booleans are.
 *
 * Three cases, and each is a decision:
 *
 *  - **The base script could not be rendered** (it holds an entry the codec
 *    could not read): the view never showed the block, so the parse saw none.
 *    Carry the base's scripts untouched — treating "not shown" as "deleted"
 *    would let one unreadable entry silently destroy the whole script.
 *  - **The author emptied the block**: that IS a deletion, so the on-start
 *    script goes. Nothing to revert once there is nothing to run.
 *  - **Otherwise**: the base's on-start script keeps its identity and takes the
 *    authored steps; other scripts are carried in their original order.
 */
function mergeAuthoredScripts(
  target: WorkflowStep,
  source: WorkflowStep,
): WorkflowScript[] {
  const baseAuthored = findAuthoredScript(source.scripts);
  const parsed = findAuthoredScript(target.scripts);

  if (!isCommandBlockRenderable(baseAuthored)) {
    return source.scripts.map((script) => cloneScript(script));
  }

  const others = source.scripts
    .filter((script) => script !== baseAuthored)
    .map((script) => cloneScript(script));

  if (parsed === null || parsed.steps.length === 0) return others;

  const merged = baseAuthored === null ? new WorkflowScript() : cloneScript(baseAuthored);
  merged.trigger = "on_start";
  merged.steps = parsed.steps;
  // A step that never had a script needs an id: it is required, must be unique
  // within the step, and is what `play_workflow_script` selects on.
  if (merged.id.trim().length === 0) merged.id = mintAuthoredScriptId(others);

  // The on-start script leads: it is what plays when the learner arrives.
  return [merged, ...others];
}

/** A script id unique within the step, readable rather than a GUID. */
function mintAuthoredScriptId(existing: ReadonlyArray<WorkflowScript>): string {
  const taken = new Set(existing.map((script) => script.id.trim().toLowerCase()));
  if (!taken.has("on-start")) return "on-start";
  for (let n = 2; ; n += 1) {
    const candidate = `on-start-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

function carryMachineData(target: WorkflowStep, source: WorkflowStep): void {
  target.stepId = source.stepId;
  target.scripts = mergeAuthoredScripts(target, source);
  // Instructions the view could not project are carried, exactly like an
  // unreadable command entry: the block was never rendered, so the parse read
  // nothing and a save would otherwise DELETE prose the author can still see in
  // raw mode. Gated on the BASE being unrenderable rather than on the parsed
  // value being empty, or emptying the block in the editor could never actually
  // empty it.
  // NOTE the explicit null test: `isInstructionsBlockRenderable(null)` is false
  // — there is no block to draw — but an ABSENT value has nothing to carry, and
  // carrying it would discard whatever the author just typed into an empty
  // block. The gate is "the base had prose the view could not show", not "the
  // view drew no block".
  if (
    source.stepInstructions !== null
    && !isInstructionsBlockRenderable(source.stepInstructions)
  ) {
    target.stepInstructions = source.stepInstructions;
  }
  // The video transcript follows the instructions rule one line up, and needs
  // the carry for the same reason: when the block was not rendered the parse
  // read nothing, so a save would DELETE prose the author can still see in raw
  // mode. Gated on the BASE being unprojectable — not on the parsed value being
  // empty — or emptying the block in the editor could never actually empty it.
  //
  // Note this reaches through `media`, which the parse rebuilds from the
  // rendered lines: a step whose media block was emptied in the editor has
  // `target.media === null` and there is nothing to carry onto, which is
  // correct — the author deleted the media, transcript and all.
  if (
    target.media !== null
    && source.media !== null
    && !isTranscriptBlockRenderable(source.media.transcript)
  ) {
    target.media.transcript = source.media.transcript;
  }
  // Unrendered like the header booleans above, and dropped for the same reason
  // if they are not carried: the step's override of the workflow's input
  // setting, whether pressing Next here ends the run, what has to happen before
  // Next is available at all, and whether the step is parked out of the run.
  target.userInputAllowed =
    source.userInputAllowed === null ? null : [...source.userInputAllowed];
  target.invalidUserInputAllowed = [...source.invalidUserInputAllowed];
  target.isCompletionStep = source.isCompletionStep;
  target.completionCondition = source.completionCondition;
  target.disabled = source.disabled;
  // Also unrendered, and the reason it stays that way: `step_view` is six
  // captured floats with no hand-typed form — the agent takes them from the
  // live camera. Rendering them would mean a coordinate grammar in a
  // prose-shaped editor, and the round trip would round them, for a value the
  // author never types. Deep-copied rather than shared, so the projection keeps
  // its "never shares mutable state with the model" property.
  target.stepView = source.stepView === null ? null : cloneStepView(source.stepView);
}

/**
 * Deep-copies `workflow_ui` for the authored-view carry-over. Local rather than
 * exported from `WorkflowDocument.ts` so this file keeps its "the projection
 * never shares mutable state with the model" property.
 */
function cloneWorkflowUiForCarry(source: WorkflowUiState | null): WorkflowUiState | null {
  if (source === null) return null;
  const clone = new WorkflowUiState();
  clone.panelsLayout = source.panelsLayout.map((entry) => {
    const placement = new WorkflowPanelPlacement();
    placement.panel = entry.panel;
    if (entry.dock !== null) {
      const dock = new WorkflowPanelDock();
      dock.parent = entry.dock.parent;
      dock.side = entry.dock.side;
      dock.invalidSide = entry.dock.invalidSide;
      // ⚠ A manual field list with no spread, so a field added to
      // `WorkflowPanelDock` and not added HERE is stripped silently — from
      // every agent patch on one side and every WYSIWYG save on the other.
      dock.align = entry.dock.align;
      dock.invalidAlign = entry.dock.invalidAlign;
      placement.dock = dock;
    }
    if (entry.screen !== null) {
      const screen = new WorkflowPanelScreenDock();
      screen.x = entry.screen.x;
      screen.y = entry.screen.y;
      screen.invalidX = entry.screen.invalidX;
      screen.invalidY = entry.screen.invalidY;
      placement.screen = screen;
    }
    placement.position =
      entry.position === null ? null : { x: entry.position.x, y: entry.position.y };
    placement.embed = entry.embed;
    return placement;
  });
  return clone;
}
